import { getCurrentLocaleCode, trans } from "@mongez/localization";
import { useLocation } from "@remix-run/react";
import { Category } from "core/catalog";
import { cn } from "core/shared";
import { URLS } from "framework/config/urls";
import { BaseLink } from "~/design-system/components/link";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/design-system/components/ui/accordion";
import { headerMenuAtom } from "~/modules/shared/atoms";
import { MenuNode } from "~/shared/types";

interface MobileAccordionMenuProps {
  menuItem: MenuNode;
}

/* ---------------------------------------------
 * Helpers
 * --------------------------------------------- */

function MenuLink({ item }: { item: MenuNode }) {
  const { pathname, search } = useLocation();

  const currentPath = pathname + search;

  const localeCode = getCurrentLocaleCode();

  const href = item.category
    ? URLS.shop.viewCategory(item.category as Category)
    : (item.link as string);

  const localizedURL = `/${localeCode}${href}`;

  const isActive = currentPath === localizedURL;

  return (
    <BaseLink
      onClick={headerMenuAtom.close}
      to={href}
      className={cn(
        "text-night font-jost text-base font-bold transition-all hover:text-[#FF506C]",
        isActive && "text-primary-default",
      )}
    >
      {item.name}
    </BaseLink>
  );
}

function MenuLabel({ name }: { name: string }) {
  return <h6 className="pb-3 text-base font-bold text-[#FF506C]">{name}</h6>;
}

/* ---------------------------------------------
 * Recursive Renderer
 * --------------------------------------------- */

function MenuTree({ nodes }: { nodes?: MenuNode[] }) {
  if (!nodes?.length) return null;

  return (
    <ul className="flex flex-col gap-5 capitalize">
      {nodes.map((node) => (
        <li key={node.id} className="flex flex-col gap-2">
          {node.type === "label" && <MenuLabel name={node.name} />}

          {node.children ? (
            <MenuTree nodes={node.children} />
          ) : (
            <MenuLink item={node} />
          )}
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------------------
 * Main Component
 * --------------------------------------------- */

export function MobileAccordionMenu({ menuItem }: MobileAccordionMenuProps) {
  return (
    <Accordion type="single" collapsible>
      <AccordionItem value={menuItem.name}>
        <AccordionTrigger
          aria-label={trans(menuItem.name)}
          className="py-0 transition-all hover:text-[#FF506C] hover:no-underline"
        >
          <li className="font-jost text-2xl font-semibold">
            {trans(menuItem.name)}
          </li>
        </AccordionTrigger>

        <AccordionContent className="my-4 rounded-2xl bg-[#F1F1F1] px-6 transition-all duration-300 ease-in-out">
          <div className="font-jost xl:hidden">
            <Accordion type="single" collapsible>
              {menuItem.children?.map((item) => (
                <AccordionItem
                  key={item.id}
                  value={String(item.id)}
                  className="border-[#0A0A0A]/10"
                >
                  <AccordionTrigger className="text-lg transition-all hover:no-underline">
                    <h5 className="text-night font-bold uppercase transition-all hover:text-[#FF506C]">
                      {item.name}
                    </h5>
                  </AccordionTrigger>

                  <AccordionContent className="mt-0 transition-all duration-200 ease-in-out">
                    {item.children ? (
                      <MenuTree nodes={item.children} />
                    ) : (
                      <MenuLink item={item} />
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
